import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  InputBase,
  Popover,
  Radio,
  RadioGroup,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { TabletButton } from '@campfire/tablet-button';
import { capitalize } from 'lodash';
import { DataGrid, GridCellParams, GridColumns, GridFooterContainer, GridPagination } from '@material-ui/data-grid';
import {
  ArrowDownward,
  ArrowUpward,
  Assignment,
  Build,
  CheckCircle as CheckCircleIcon,
  ChevronRight,
  Close,
  Email,
  EmojiPeople,
  Event,
  ExpandMore,
  Explore,
  Forum,
  GetApp,
  HowToReg,
  LocalOffer,
  LocationOn,
  NotInterested,
  PersonAdd,
  Refresh,
  Search,
  Sms,
  ViewColumn,
  VpnKey,
  Warning,
} from '@material-ui/icons';
import Icon from '@mdi/react';
import { mdiCursorDefaultClick } from '@mdi/js';
import { DateTime } from 'luxon';
import { EditorState, convertFromRaw } from 'draft-js';
import { withRouter } from 'react-router-dom';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { VolunteerProfile } from '../../../../common/AvatarName';
import './simple-db.css';
import { RoleIcon } from '../../../../common/icons/RoleIconList';
import { TaskBadge } from '../../../admin/tasks/TaskBadge';
import { VolunteerCommonProfileDialog } from '../../volunteer-common-profile/VolunteerCommonProfileDialog';

import { TooltipMenu } from '../../../../common/tooltip-menu';
import { VolunteerCommonProfileEmailDialog } from '../../volunteer-common-profile/profile-sections/action-menu/VolunteerCommonProfileEmailDialog';
import { SendPasswordResetDialog } from './SendPasswordResetDialog';
import { VolunteerCommonProfileSmsDialog } from '../../volunteer-common-profile/profile-sections/action-menu/VolunteerCommonProfileSmsDialog';
import { useOrgInfo } from '../../../../global/auth/use-org-info/use-org-info';
import { CreateChannelDialog } from '../../../general/chat/CreateChannelDialog';
import UserCheckinIcon from '../../../../assets/icons/user-checkin.svg';
import UserCheckinLightIcon from '../../../../assets/icons/user-checkin-light.svg';
import UserCheckoutLightIcon from '../../../../assets/icons/user-checkout-light.svg';

import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { GET_PROGRAM_ACTIVITIES_VOL_DB } from '../../../../global/programs-sell/program-activites.gql';
import { GET_VOLUNTEER_TAGS_FOR_FILTER } from './volunteer-tags-filter.gql';
import { GetVolunteerTagsForFilter } from './__generated__/GetVolunteerTagsForFilter';
import { GET_TASKS_WITH_FIELDS } from '../../../admin/tasks-import/get-tasks-with-fields.gql';
import { ExportsList } from '../../../../common/export/ExportsList';
import {
  GetProgramActivitiesVolDB,
  GetProgramActivitiesVolDB_vm_programs_activities as ProgramActivityType,
  GetProgramActivitiesVolDBVariables,
} from '../../../../global/programs-sell/__generated__/GetProgramActivitiesVolDB';
import { GetTasksWithFields } from '../../../admin/tasks-import/__generated__/GetTasksWithFields';
import { useUser } from '../../../../global/auth/useUser';
import { LocationSearchDialog } from './LocationSearchDialog';
import { TaskFieldSearchDialog, TaskFieldSearchParams } from './TaskFieldSearchDialog';
import { TaskColumnSelectionDialog } from './TaskColumnSelectionDialog';
import { TaskFieldColumn } from './TaskFieldSelector';
import { isDraftJsContent } from '../../../../common/comment/CommentBox';
import { ProgramFilters } from '../../../../global/program-filters/ProgramFilters';
import { ActivityFilters } from '../../../../global/activity-filters/ActivityFilters';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { BulkRoleEnrolDialog } from './BulkRoleEnrolDialog';
import { BulkTagDialog, BulkTagMode } from './BulkTagDialog';
import { BulkTaskActionDialog, BulkTaskAction } from './BulkTaskActionDialog';
import { BulkCheckInDialog } from './BulkCheckInDialog';
import { BulkCheckOutDialog } from './BulkCheckOutDialog';

interface VolunteerDatabaseArray {
  total: number;
  volunteers: GetVolunteerDatabaseVolunteer[];
}

interface VolunteerTagCell {
  volunteerTagId: string;
  name: string;
  colour: string | null;
  tagGroup: string | null;
}

interface RoleType {
  roleId: string;
  roleName: string;
  roleIcon: string | null;
  completed: boolean;
}

interface ActivityType {
  activityId: string;
  name: string;
  programId: string;
  programName: string;
}

interface ProgramType {
  programId: string;
  programName: string;
}

interface GetVolunteerDatabaseVolunteer {
  id: string;
  volunteerId: string;
  profileId: string;
  userId: string;
  avatarUrl: string;
  isDeactivated: boolean;
  isFlagged: boolean;
  firstName: string;
  status: { string: string; date: { date: string } };
  preferredName: string | null;
  lastName: string;
  email: string;
  dob: string | null;
  contactNumber: string | null;
  dateCreated: { date: string };
  commentCount: number;
  roles: RoleType[];
  activities: ActivityType[];
  programs: ProgramType[];
  isCheckedIn?: boolean;
  distance?: number; // Distance in km when location search is active
  description?: string; // Optional: profile description
  volunteerHours?: number; // Optional: total volunteer hours
  lastAttended?: { date: string } | null; // Optional: last attendance date
  totalAttendances?: number; // Optional: total attendance count
  enrolledActivitiesCount?: number; // Optional: enrolled activities count
  lengthOfService?: string; // Optional: length of service in years and months
  waitlistings?: Array<{ activityId: string; name: string; programName: string; dateAdded: string }>;
  applications?: Array<{ activityId: string; name: string; programName: string; dateApplied: string }>;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    hideRightSeparator: {
      '.MuiDataGrid-columnSeparator': {
        display: 'none !important',
      },
    },
    edit: {
      color: theme.alert.blue.extraLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      '& span': {
        fontSize: '14px',
      },
      '& p': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
      textTransform: 'none',
    },
    disabledLabel: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      '& span': {
        fontSize: '14px',
      },
      '& p': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
      '& .Mui-disabled': {
        color: 'rgba(255, 255, 255, 0.6) !important',
      },
      textTransform: 'none',
      cursor: 'not-allowed',
    },
  })
);

// Helper function to convert Draft.js description to plain text
const descriptionToPlainText = (description: string | null | undefined): string => {
  if (!description) {
    return '';
  }
  try {
    if (isDraftJsContent(description)) {
      return EditorState.createWithContent(convertFromRaw(JSON.parse(description)))
        .getCurrentContent()
        .getPlainText();
    }
    return description;
  } catch (error) {
    // If parsing fails, return the original string or empty
    return description || '';
  }
};

const VolunteerDatabaseScreenSimple = withRouter(() => {
  const classes = useStyles();
  const { theme, isMobile, isSm } = useCampfireTheme();

  const [volunteerDatabase, setVolunteerDatabase] = useState<VolunteerDatabaseArray | undefined>(undefined);
  const [limit, setLimit] = useState<number>(25);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterOptionsReady, setFilterOptionsReady] = useState<boolean>(false);
  const [refetchToken, setRefetchToken] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filterString, setFilterString] = useState<string>('');
  const [profileOpen, setProfileOpen] = useState<boolean>(false);
  const [openProfilePage, setOpenProfilePage] = useState<string | undefined>();
  const [openProfileId, setOpenProfileId] = useState<string | null>(null);
  const [openUserId, setOpenUserId] = useState<string | null>(null);

  const [openVolunteers, setOpenVolunteers] = React.useState<boolean>(false);
  const [openPrograms, setOpenPrograms] = React.useState<boolean>(false);
  const [openActivities, setOpenActivities] = React.useState<boolean>(false);
  const [openRoles, setOpenRoles] = React.useState<boolean>(false);
  const [openTags, setOpenTags] = React.useState<boolean>(false);
  const [openTools, setOpenTools] = React.useState<boolean>(false);
  const [openColumns, setOpenColumns] = React.useState<boolean>(false);
  const [exportsPopoverAnchor, setExportsPopoverAnchor] = React.useState<HTMLElement | null>(null);
  const [locationSearchOpen, setLocationSearchOpen] = React.useState<boolean>(false);
  const [taskFieldSearchOpen, setTaskFieldSearchOpen] = React.useState<boolean>(false);
  const [taskColumnSelectionOpen, setTaskColumnSelectionOpen] = React.useState<boolean>(false);

  const [includeInactive, setIncludeInactive] = React.useState<string>('active');
  const [programFilter, setProgramFilter] = React.useState<Array<string>>([]);
  const [exclusiveProgramFilter, setExclusiveProgramFilter] = React.useState<boolean>(false);
  const [activityFilter, setActivityFilter] = React.useState<Array<string>>([]);
  const [roleFilter, setRoleFilter] = React.useState<Array<string>>([]);
  const [orderBy, setOrderBy] = React.useState<string>('name');
  const [orderDirection, setOrderDirection] = React.useState<boolean>(true);

  const [programSearchQuery, setProgramSearchQuery] = React.useState<string>('');
  const [activitySearchQuery, setActivitySearchQuery] = React.useState<string>('');
  const [roleSearchQuery, setRoleSearchQuery] = React.useState<string>('');

  // Waitlisting/Application activity filters
  const [waitlistActivityFilter, setWaitlistActivityFilter] = React.useState<Array<string>>([]);
  const [applicationActivityFilter, setApplicationActivityFilter] = React.useState<Array<string>>([]);
  const [waitlistActivitySearchQuery, setWaitlistActivitySearchQuery] = React.useState<string>('');
  const [applicationActivitySearchQuery, setApplicationActivitySearchQuery] = React.useState<string>('');
  const [openWaitlistActivities, setOpenWaitlistActivities] = React.useState(false);
  const [openApplicationActivities, setOpenApplicationActivities] = React.useState(false);

  // Location search filters
  const [locationFilter, setLocationFilter] = React.useState<{
    lat: number;
    long: number;
    range: number;
    sort: 'distance' | 'distance_desc' | 'alphabetical';
    addressTaskFieldId: string;
  } | null>(null);

  // Checked-in filter
  const [checkedInFilter, setCheckedInFilter] = React.useState(false);

  // Bulk actions
  const [actionsEnabled, setActionsEnabled] = React.useState(false);
  const [selectedVolunteersMap, setSelectedVolunteersMap] = React.useState<
    Record<string, GetVolunteerDatabaseVolunteer>
  >({});
  const [bulkEmailOpen, setBulkEmailOpen] = React.useState(false);
  const [passwordResetOpen, setPasswordResetOpen] = React.useState(false);
  const [bulkSmsOpen, setBulkSmsOpen] = React.useState(false);
  const [createChatOpen, setCreateChatOpen] = React.useState(false);
  const [openActionComms, setOpenActionComms] = React.useState(false);
  const [openActionPrograms, setOpenActionPrograms] = React.useState(false);
  const [openActionActivities, setOpenActionActivities] = React.useState(false);
  const [openActionPeople, setOpenActionPeople] = React.useState(false);
  const [openActionTasks, setOpenActionTasks] = React.useState(false);
  const [openActionAttendance, setOpenActionAttendance] = React.useState(false);
  const [bulkProgramEnrolOpen, setBulkProgramEnrolOpen] = React.useState(false);
  const [bulkActivityEnrolOpen, setBulkActivityEnrolOpen] = React.useState(false);
  const [bulkRoleEnrolOpen, setBulkRoleEnrolOpen] = React.useState(false);
  const [bulkTagMode, setBulkTagMode] = React.useState<BulkTagMode | null>(null);
  const [bulkTaskOpen, setBulkTaskOpen] = React.useState(false);
  const [bulkTaskAction, setBulkTaskAction] = React.useState<BulkTaskAction>('approved');
  const [bulkCheckInOpen, setBulkCheckInOpen] = React.useState(false);
  const [bulkCheckOutOpen, setBulkCheckOutOpen] = React.useState(false);

  const closeAllActionMenus = () => {
    setOpenActionComms(false);
    setOpenActionPrograms(false);
    setOpenActionActivities(false);
    setOpenActionPeople(false);
    setOpenActionTasks(false);
    setOpenActionAttendance(false);
  };

  const peopleActions: {
    label: string;
    icon: JSX.Element;
    onSelect: () => void;
    dividerBefore?: boolean;
  }[] = [
    {
      label: 'Add Role',
      icon: <PersonAdd style={{ fontSize: '16px' }} />,
      onSelect: () => setBulkRoleEnrolOpen(true),
    },
    {
      label: 'Add Tag',
      icon: <LocalOffer style={{ fontSize: '16px' }} />,
      onSelect: () => setBulkTagMode('add'),
      dividerBefore: true,
    },
    {
      label: 'Remove Tag',
      icon: <LocalOffer style={{ fontSize: '16px' }} />,
      onSelect: () => setBulkTagMode('remove'),
    },
  ];

  const selectedVolunteers = React.useMemo(() => Object.values(selectedVolunteersMap), [selectedVolunteersMap]);

  const selectedVolunteerIds = React.useMemo(() => Object.keys(selectedVolunteersMap), [selectedVolunteersMap]);

  // The v4 DataGrid can only render checks for rows it currently holds, so feed it just the
  // current page's selected ids. The full cross-page selection lives in selectedVolunteersMap.
  const currentPageSelectionModel = React.useMemo(
    () => (volunteerDatabase?.volunteers ?? []).filter((v) => selectedVolunteersMap[String(v.id)]).map((v) => v.id),
    [volunteerDatabase, selectedVolunteersMap]
  );

  const clearSelectedVolunteers = React.useCallback(() => setSelectedVolunteersMap({}), []);

  // Merge selection changes for the current page without dropping selections made on other pages.
  // Returns the previous map (same reference) when nothing changed to avoid an update loop —
  // the v4 DataGrid re-fires onSelectionModelChange with off-page ids stripped on every render.
  const handleSelectionModelChange = React.useCallback(
    (newSelection: (string | number)[]) => {
      const currentRows = volunteerDatabase?.volunteers ?? [];
      const newSelectionSet = new Set(newSelection.map((id) => String(id)));
      setSelectedVolunteersMap((prev) => {
        const next = { ...prev };
        let changed = false;
        currentRows.forEach((v) => {
          const id = String(v.id);
          const isSelected = newSelectionSet.has(id);
          if (isSelected && !next[id]) {
            next[id] = v;
            changed = true;
          } else if (!isSelected && next[id]) {
            delete next[id];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    },
    [volunteerDatabase]
  );

  const selectedVolunteersForDialog = React.useMemo(
    () =>
      selectedVolunteers.map((v) => ({
        volunteerId: v.volunteerId,
        profile: {
          profileId: v.profileId,
          preferredName: v.preferredName || v.firstName,
          lastName: v.lastName,
          avatarUrl: v.avatarUrl,
        },
      })),
    [selectedVolunteers]
  );

  // Task field search filters
  const [taskFieldFilter, setTaskFieldFilter] = React.useState<TaskFieldSearchParams | null>(null);

  // Task field columns
  const [taskFieldColumns, setTaskFieldColumns] = React.useState<TaskFieldColumn[]>([]);

  // Track which field IDs were added by search (so we can remove only those when search is cleared)
  const [searchAddedFieldIds, setSearchAddedFieldIds] = React.useState<Set<string>>(new Set());

  // Optional columns - all optional columns are disabled by default
  const [optionalColumns, setOptionalColumns] = React.useState<{
    dateOfBirth: boolean;
    description: boolean;
    volunteerHours: boolean;
    lastAttended: boolean;
    totalAttendances: boolean;
    enrolledActivitiesCount: boolean;
    lengthOfService: boolean;
    volunteerId: boolean;
    profileId: boolean;
    waitlistings: boolean;
    applications: boolean;
    attendanceRecords: boolean;
    tags: boolean;
  }>({
    dateOfBirth: false,
    description: false,
    volunteerHours: false,
    lastAttended: false,
    totalAttendances: false,
    enrolledActivitiesCount: false,
    lengthOfService: false,
    volunteerId: false,
    profileId: false,
    waitlistings: false,
    applications: false,
    attendanceRecords: false,
    tags: false,
  });
  const [tagFilter, setTagFilter] = React.useState<string[]>([]);
  const [tagFilterLogic, setTagFilterLogic] = React.useState<'any' | 'all'>('any');
  const [tagSearchQuery, setTagSearchQuery] = React.useState<string>('');
  const [attendanceStartDate, setAttendanceStartDate] = React.useState<string | null>(null);
  const [attendanceEndDate, setAttendanceEndDate] = React.useState<string | null>(null);

  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });
  const { setSnackbar } = useSnackbar();

  const bulkVolunteerIds = selectedVolunteers.map((v) => v.volunteerId);
  const bulkProfileIds = selectedVolunteers.map((v) => v.profileId);
  const bulkAttendanceVolunteers = selectedVolunteers.map((v) => ({
    volunteerId: v.volunteerId,
    name: `${v.preferredName ?? v.firstName} ${v.lastName}`,
  }));

  const handleBulkProgramEnrol = (programIds: string[]) => {
    campfireMutation
      .run({
        method: 'post',
        url: `${apiUrl}/vm/program/enrol`,
        data: { programIds, volunteerIds: bulkVolunteerIds },
      })
      .then((res) => {
        if (res.ok) {
          setSnackbar({ open: true, message: 'Volunteers enrolled in program(s)', variant: 'success' });
          fetch();
        } else {
          setSnackbar({ open: true, message: 'Failed to enrol in program(s)', variant: 'error' });
        }
      });
  };

  const handleBulkActivityEnrol = (activityIds: string[]) => {
    campfireMutation
      .run({
        method: 'post',
        url: `${apiUrl}/vm/activity/enrolment/enrol`,
        data: { activityIds, volunteerIds: bulkVolunteerIds },
      })
      .then((res) => {
        if (res.ok) {
          setSnackbar({ open: true, message: 'Volunteers enrolled in activity/ies', variant: 'success' });
          fetch();
        } else {
          setSnackbar({ open: true, message: 'Failed to enrol in activity/ies', variant: 'error' });
        }
      });
  };

  // Defer loading filter data until after volunteer database loads
  const { data: programActivityData } = useCampfireQuery<GetProgramActivitiesVolDB, GetProgramActivitiesVolDBVariables>(
    GET_PROGRAM_ACTIVITIES_VOL_DB,
    {
      options: {
        skip: !filterOptionsReady,
      },
    }
  );

  const { data: volunteerTagData } = useCampfireQuery<GetVolunteerTagsForFilter, {}>(GET_VOLUNTEER_TAGS_FOR_FILTER, {
    options: {
      skip: !filterOptionsReady,
    },
  });

  // Shared query for task field dialogs - defer until after volunteer database loads
  const { data: tasksWithFieldsData } = useCampfireQuery<GetTasksWithFields, undefined>(GET_TASKS_WITH_FIELDS, {
    options: {
      skip: !filterOptionsReady,
    },
  });

  const { getManagerIdentity, user } = useUser();
  const { programManagerIdentities } = getManagerIdentity();
  const managedProgramIds = programManagerIdentities.flatMap((p) => p.programId);
  const orgInfo = useOrgInfo();

  const activityRoles = programActivityData?.vm.activityRoles;

  const filteredProgramIds = user.userIdentityService.isProgramManager
    ? programActivityData?.vm.programs.filter((p) => managedProgramIds.includes(p.programId))
    : programActivityData?.vm.programs;

  const activitiesFilterData = filteredProgramIds?.flatMap((p) =>
    p.activities.flatMap((a) => {
      return a;
    })
  );

  const sharedProgramIds = React.useMemo(() => {
    if (selectedVolunteers.length === 0) return new Set<string>();
    const sets = selectedVolunteers.map((v) => new Set(v.programs.map((p) => p.programId)));
    return new Set([...sets[0]].filter((id) => sets.every((s) => s.has(id))));
  }, [selectedVolunteers]);

  const activitiesForBulkEnrol = filteredProgramIds
    ?.filter((p) => sharedProgramIds.has(p.programId))
    .flatMap((p) => p.activities.map((a) => ({ ...a, program: { ...a.program, name: p.name } }))) as any;

  const [activityFilterOptions, setActivityFilterOptions] = React.useState<ProgramActivityType[] | null>(null);

  if (programActivityData && activitiesFilterData && activityFilterOptions === null) {
    setActivityFilterOptions(activitiesFilterData);
  }

  const searchFilteredPrograms = filteredProgramIds?.filter((program) =>
    program.name.toLowerCase().includes(programSearchQuery.toLowerCase())
  );

  const searchFilteredActivities = activityFilterOptions?.filter((activity) =>
    activity.name.toLowerCase().includes(activitySearchQuery.toLowerCase())
  );

  const searchFilteredRoles = activityRoles?.filter((role) =>
    role.name.toLowerCase().includes(roleSearchQuery.toLowerCase())
  );

  const volunteerTagOptions = volunteerTagData?.vm.volunteerTags ?? [];
  const searchFilteredTags = volunteerTagOptions.filter((tag) =>
    tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase())
  );

  const searchFilteredWaitlistActivities = activityFilterOptions?.filter((activity) =>
    activity.name.toLowerCase().includes(waitlistActivitySearchQuery.toLowerCase())
  );

  const searchFilteredApplicationActivities = activityFilterOptions?.filter((activity) =>
    activity.name.toLowerCase().includes(applicationActivitySearchQuery.toLowerCase())
  );

  function fetch(newPage?: number) {
    setLoading(true);
    setError(null);
    campfireMutation
      .run({
        url: `${apiUrl}/vm/management/volunteer-database`,
        method: 'post',
        data: {
          filterByString: filterString,
          filterByProgramIds: programFilter,
          filterByActivityIds: activityFilter,
          filterByRoles: roleFilter,
          filterByTagIds: tagFilter,
          filterByTagLogic: tagFilterLogic,
          filterByWaitlistedActivityIds: waitlistActivityFilter,
          filterByAppliedActivityIds: applicationActivityFilter,
          exclusiveProgramFilter: exclusiveProgramFilter && programFilter.length > 0,
          includeInactive: includeInactive === 'all' || includeInactive === 'inactive',
          onlyInactive: includeInactive === 'inactive',
          orderBy: orderBy,
          orderDirection: orderDirection,
          offset: newPage === undefined ? currentPage * 25 : newPage * 25,
          limit: 25,
          // Location search parameters
          ...(locationFilter && {
            locationLat: locationFilter.lat,
            locationLong: locationFilter.long,
            locationRange: locationFilter.range,
            addressTaskFieldId: locationFilter.addressTaskFieldId,
          }),
          // Optional columns
          optionalColumns: {
            dateOfBirth: optionalColumns.dateOfBirth,
            description: optionalColumns.description,
            volunteerHours: optionalColumns.volunteerHours,
            lastAttended: optionalColumns.lastAttended,
            totalAttendances: optionalColumns.totalAttendances,
            enrolledActivitiesCount: optionalColumns.enrolledActivitiesCount,
            lengthOfService: optionalColumns.lengthOfService,
            waitlistings: optionalColumns.waitlistings,
            applications: optionalColumns.applications,
            volunteerId: optionalColumns.volunteerId,
            profileId: optionalColumns.profileId,
            attendanceRecords: optionalColumns.attendanceRecords,
            tags: optionalColumns.tags,
          },
          ...(optionalColumns.attendanceRecords &&
            attendanceStartDate && {
              attendanceStartDate,
            }),
          ...(optionalColumns.attendanceRecords &&
            attendanceEndDate && {
              attendanceEndDate,
            }),
          // Checked-in filter
          ...(checkedInFilter && { checkedInOnly: true }),
          // Task/Field search parameters
          ...(taskFieldFilter && {
            taskFieldFilters: taskFieldFilter.filters,
            taskFieldFilterLogic: taskFieldFilter.filterLogic,
          }),
          // Task field columns - pass taskId and fieldId to backend
          ...(taskFieldColumns.length > 0 && {
            taskFieldColumns: taskFieldColumns.map((col) => ({
              taskId: col.taskId,
              fieldId: col.fieldId,
            })),
          }),
        },
      })
      .then((response) => {
        if (response && response.data && response.data.data) {
          setVolunteerDatabase(response.data.data as VolunteerDatabaseArray);
          setError(null);
        } else {
          setError('Failed to load volunteer database data');
        }
        setLoading(false);
        setFilterOptionsReady(true);
      })
      .catch((err) => {
        console.error('Volunteer database query error:', err);
        setError('Error loading volunteer database. Please try again.');
        setLoading(false);
        setFilterOptionsReady(true);
      });
  }

  function clear() {
    setVolunteerDatabase(undefined);
    setRefetchToken((token) => token + 1);
  }

  function reset() {
    setProgramFilter([]);
    setExclusiveProgramFilter(false);
    setActivityFilter([]);
    setRoleFilter([]);
    setTagFilter([]);
    setTagFilterLogic('any');
    setTagSearchQuery('');
    setWaitlistActivityFilter([]);
    setApplicationActivityFilter([]);
    setFilterString('');
    setIncludeInactive('active');
    setLocationFilter(null);
    setCheckedInFilter(false);
    setTaskFieldFilter(null);
    setTaskFieldColumns([]);
    setSearchAddedFieldIds(new Set());
    setOptionalColumns({
      dateOfBirth: false,
      description: false,
      volunteerHours: false,
      lastAttended: false,
      totalAttendances: false,
      enrolledActivitiesCount: false,
      lengthOfService: false,
      volunteerId: false,
      profileId: false,
      waitlistings: false,
      applications: false,
      attendanceRecords: false,
      tags: false,
    });
    clearSelectedVolunteers();
    setActionsEnabled(false);
    setCurrentPage(0);
    clear();
  }

  useEffect(() => {
    fetch();
  }, [refetchToken]);

  useEffect(() => {
    if (activitiesFilterData && programFilter.length > 0) {
      setActivityFilterOptions(activitiesFilterData?.filter((a) => programFilter.includes(a.program.programId)));
    }
  }, [volunteerDatabase]);

  function changePage(page: number) {
    setCurrentPage(page);
    fetch(page);
  }

  function programFilterChange(programId: string) {
    if (programFilter.includes(programId)) {
      setProgramFilter(
        programFilter.filter((a) => {
          return a !== programId;
        })
      );
    } else {
      setProgramFilter([...programFilter, programId]);
    }
    setCurrentPage(0);
    clear();
  }

  function activityFilterChange(activityId: string) {
    if (activityFilter.includes(activityId)) {
      setActivityFilter(
        activityFilter.filter((a) => {
          return a !== activityId;
        })
      );
    } else {
      setActivityFilter([...activityFilter, activityId]);
    }
    setCurrentPage(0);
    clear();
  }

  function roleFilterChange(roleId: string) {
    if (roleFilter.includes(roleId)) {
      setRoleFilter(
        roleFilter.filter((r) => {
          return r !== roleId;
        })
      );
    } else {
      setRoleFilter([...roleFilter, roleId]);
    }
    setCurrentPage(0);
    clear();
  }

  function tagFilterChange(tagId: string) {
    const isSelected = tagFilter.includes(tagId);
    setTagFilter(isSelected ? tagFilter.filter((t) => t !== tagId) : [...tagFilter, tagId]);

    if (!isSelected) {
      setOptionalColumns((prev) => (prev.tags ? prev : { ...prev, tags: true }));
    }

    setCurrentPage(0);
    clear();
  }

  function tagFilterLogicChange(logic: 'any' | 'all') {
    setTagFilterLogic(logic);
    if (tagFilter.length > 1) {
      setCurrentPage(0);
      clear();
    }
  }

  function waitlistActivityFilterChange(activityId: string) {
    if (waitlistActivityFilter.includes(activityId)) {
      setWaitlistActivityFilter(waitlistActivityFilter.filter((a) => a !== activityId));
    } else {
      setWaitlistActivityFilter([...waitlistActivityFilter, activityId]);
    }
    setCurrentPage(0);
    clear();
  }

  function applicationActivityFilterChange(activityId: string) {
    if (applicationActivityFilter.includes(activityId)) {
      setApplicationActivityFilter(applicationActivityFilter.filter((a) => a !== activityId));
    } else {
      setApplicationActivityFilter([...applicationActivityFilter, activityId]);
    }
    setCurrentPage(0);
    clear();
  }

  function updateLimit(number: number) {
    setLimit(number);
    clear();
  }

  function handleDownloadButtonClick(event: React.MouseEvent<HTMLElement>) {
    setExportsPopoverAnchor(event.currentTarget);
  }

  function handleExportsPopoverClose() {
    setExportsPopoverAnchor(null);
  }

  function toggleOptionalColumn(columnKey: keyof typeof optionalColumns) {
    setOptionalColumns((prev) => {
      const newValue = !prev[columnKey];
      // Clear corresponding filter when column is toggled off
      if (!newValue) {
        if (columnKey === 'waitlistings') {
          setWaitlistActivityFilter([]);
        }
        if (columnKey === 'applications') {
          setApplicationActivityFilter([]);
        }
      }
      return { ...prev, [columnKey]: newValue };
    });
    clear(); // Refresh data when columns change
  }

  function handleCsvDownloadRequest() {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/management/volunteer-database`,
        method: 'post',
        data: {
          filterByString: filterString,
          filterByProgramIds: programFilter,
          filterByActivityIds: activityFilter,
          filterByRoles: roleFilter,
          filterByTagIds: tagFilter,
          filterByTagLogic: tagFilterLogic,
          filterByWaitlistedActivityIds: waitlistActivityFilter,
          filterByAppliedActivityIds: applicationActivityFilter,
          exclusiveProgramFilter: exclusiveProgramFilter && programFilter.length > 0,
          includeInactive: includeInactive === 'all' || includeInactive === 'inactive',
          onlyInactive: includeInactive === 'inactive',
          orderBy: orderBy,
          orderDirection: orderDirection,
          offset: 0,
          limit: 25,
          download: true,
          // Location search parameters for export
          ...(locationFilter && {
            locationLat: locationFilter.lat,
            locationLong: locationFilter.long,
            locationRange: locationFilter.range,
            addressTaskFieldId: locationFilter.addressTaskFieldId,
          }),
          // Optional columns for export
          optionalColumns: {
            dateOfBirth: optionalColumns.dateOfBirth,
            description: optionalColumns.description,
            volunteerHours: optionalColumns.volunteerHours,
            lastAttended: optionalColumns.lastAttended,
            totalAttendances: optionalColumns.totalAttendances,
            enrolledActivitiesCount: optionalColumns.enrolledActivitiesCount,
            lengthOfService: optionalColumns.lengthOfService,
            waitlistings: optionalColumns.waitlistings,
            applications: optionalColumns.applications,
            volunteerId: optionalColumns.volunteerId,
            profileId: optionalColumns.profileId,
            attendanceRecords: optionalColumns.attendanceRecords,
            tags: optionalColumns.tags,
          },
          ...(optionalColumns.attendanceRecords &&
            attendanceStartDate && {
              attendanceStartDate,
            }),
          ...(optionalColumns.attendanceRecords &&
            attendanceEndDate && {
              attendanceEndDate,
            }),
          // Task/Field search parameters for export
          ...(taskFieldFilter && {
            taskFieldFilters: taskFieldFilter.filters,
            taskFieldFilterLogic: taskFieldFilter.filterLogic,
          }),
          // Task field columns for export
          ...(taskFieldColumns.length > 0 && {
            taskFieldColumns: taskFieldColumns.map((col) => ({
              taskId: col.taskId,
              fieldId: col.fieldId,
              fieldName: col.fieldName,
            })),
          }),
        },
      })
      .then((response) => {
        if (response && response.data && response.data.data) {
          // The response should contain an exportId that we can use to track the download
        }
      });
  }

  function profileOpenTrigger(page: string, userId: string, setProfileId: string) {
    setOpenProfilePage(page);
    setOpenProfileId(setProfileId);
    setOpenUserId(userId);
    setProfileOpen(true);
  }

  function handleLocationSearchClick() {
    setOpenTools(false);
    setLocationSearchOpen(true);
  }

  function handleTaskFieldSearchClick() {
    setOpenTools(false);
    setTaskFieldSearchOpen(true);
  }

  function handleTaskFieldSearch(params: TaskFieldSearchParams) {
    setTaskFieldFilter(params);

    // Automatically add searched fields as columns (merge with existing, avoid duplicates)
    const newColumns: TaskFieldColumn[] = params.filters.map((filter) => ({
      fieldId: filter.fieldId,
      fieldName: filter.fieldName,
      fieldType: filter.fieldType,
      taskId: filter.taskId,
      taskTitle: filter.taskTitle,
    }));

    // Track which field IDs are being added by this search
    const newFieldIds = new Set(newColumns.map((col) => col.fieldId));
    setSearchAddedFieldIds(newFieldIds);

    // Merge with existing columns, avoiding duplicates
    const existingFieldIds = new Set(taskFieldColumns.map((col) => col.fieldId));
    const columnsToAdd = newColumns.filter((col) => !existingFieldIds.has(col.fieldId));
    setTaskFieldColumns([...taskFieldColumns, ...columnsToAdd]);

    setCurrentPage(0);
    clear();
  }

  function handleTaskColumnSelection(fields: TaskFieldColumn[]) {
    setTaskFieldColumns(fields);
    clear();
  }

  function handleLocationSearch(params: {
    lat: number;
    long: number;
    range: number;
    sort: 'distance' | 'distance_desc' | 'alphabetical';
    addressTaskFieldId: string;
  }) {
    setLocationFilter(params);
    // When location search is active, update the orderBy to reflect the sort preference
    if (params.sort !== 'alphabetical') {
      setOrderBy('distance');
      setOrderDirection(params.sort === 'distance'); // true for closest, false for furthest
    } else {
      setOrderBy('name');
      setOrderDirection(true);
    }
    setCurrentPage(0);
    clear();
  }

  function setOrder(type: string) {
    if (!orderDirection && type === orderBy) {
      setOrderDirection(true);
      setOrderBy('name');
    }
    if (type !== orderBy) {
      setOrderDirection(true);
      setOrderBy(type);
    } else {
      setOrderDirection(!orderDirection);
      setOrderBy(type);
    }
    clear();
  }

  const columns: GridColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      disableColumnMenu: true,
      minWidth: 180,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return (
          <Box
            onClick={() => {
              setOrder('name');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
          >
            <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Name</Typography>
            {orderBy === 'name' ? (
              orderDirection ? (
                <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
              ) : (
                <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
              )
            ) : null}
          </Box>
        );
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const today = DateTime.local();
        const dateOfBirth = row.dob ? DateTime.fromJSDate(new Date(Date.parse(row.dob))) : null;
        const isBirthday = dateOfBirth ? dateOfBirth.month === today.month && dateOfBirth.day === today.day : false;

        let description = row.isFlagged
          ? 'Flagged & Exited'
          : row.isDeactivated
          ? 'Retired'
          : row.privilegeLevel ?? 'Volunteer';
        if (isBirthday && !row.isFlagged && !row.isDeactivated) {
          description = `${row.privilegeLevel ?? 'Volunteer'} 🎂`;
        }

        return (
          <VolunteerProfile
            inTable
            noTrim
            profileId={row.profileId}
            name={`${row.preferredName ?? row.firstName} ${row.lastName}`}
            userId={row.userId}
            lastName={row.lastName}
            preferredName={row.preferredName ?? row.firstName}
            avatarUrl={row.avatarUrl}
            description={description}
          />
        );
      },
    },
    ...(locationFilter
      ? [
          {
            field: 'distance',
            headerName: 'Distance',
            width: 100,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return (
                <Box
                  onClick={() => {
                    setOrder('distance');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Distance</Typography>
                  {orderBy === 'distance' ? (
                    orderDirection ? (
                      <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
                    ) : (
                      <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
                    )
                  ) : null}
                </Box>
              );
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              if (!row.distance) {
                return <Typography style={{ fontSize: '13px' }}>-</Typography>;
              }

              // Distance comes from backend in meters
              const distanceInMeters = row.distance;
              const displayText =
                distanceInMeters > 999
                  ? `${(distanceInMeters / 1000).toFixed(1)} km`
                  : `${Math.round(distanceInMeters)} m`;

              return <Typography style={{ fontSize: '13px' }}>{displayText}</Typography>;
            },
          },
        ]
      : []),
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 120,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return (
          <Box
            onClick={() => {
              setOrder('email');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
          >
            <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Email</Typography>
            {orderBy === 'email' ? (
              orderDirection ? (
                <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
              ) : (
                <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
              )
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'contactNumber',
      headerName: 'Phone',
      width: 130,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Phone</Typography>;
      },
    },
    {
      field: 'dateCreated',
      headerName: 'Created',
      width: 100,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return (
          <Box
            onClick={() => {
              setOrder('date');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
          >
            <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Created</Typography>
            {orderBy === 'date' ? (
              orderDirection ? (
                <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
              ) : (
                <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
              )
            ) : null}
          </Box>
        );
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const today = DateTime.local();
        const createdDate = DateTime.fromJSDate(new Date(Date.parse(row.created.date)));
        const isAnniversary =
          createdDate.hasSame(today, 'month') &&
          createdDate.hasSame(today, 'day') &&
          !createdDate.hasSame(today, 'year');

        return (
          <Typography style={{ fontSize: '13px' }}>
            {createdDate.toFormat('d MMM yyyy')}
            {isAnniversary && ' 🎉'}
          </Typography>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Status</Typography>;
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        if (row.isCheckedIn) {
          return (
            <Box
              display='flex'
              alignItems='center'
              style={{ cursor: 'pointer' }}
              onClick={() => profileOpenTrigger('attendance', row.userId, row.profileId)}
            >
              <Box
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#4caf50',
                  marginRight: 6,
                  animation: 'pulse-dot 2s ease-in-out infinite',
                }}
              />
              <Typography style={{ fontSize: '13px' }}>Checked In</Typography>
            </Box>
          );
        }
        return (
          <Box>
            {row.status.date === null ? (
              <Typography style={{ fontSize: '13px', color: '#646464' }}>{row.status.string}</Typography>
            ) : (
              <>
                <Typography style={{ fontSize: '12px', color: '#646464' }}>{row.status.string}</Typography>
                <Typography style={{ fontSize: '13px' }}>
                  {DateTime.fromJSDate(new Date(Date.parse(row.status.date.date))).toFormat('d LLLL yyyy')}
                </Typography>
              </>
            )}
          </Box>
        );
      },
    },
    {
      field: 'comments',
      headerName: 'Comments',
      width: 60,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}></Typography>;
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        return (
          <div
            onClick={() => profileOpenTrigger('comments', row.userId, row.profileId)}
            style={{ display: 'contents', cursor: 'pointer' }}
          >
            <Box style={{ display: 'flex', flex: '1', justifyContent: 'center' }}>
              <svg width='19' height='18' viewBox='0 0 19 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M9.17279 0C14.2387 0 18.3456 3.31835 18.3456 7.41177C18.3456 9.72424 17.0339 11.7889 14.9802 13.1479C15.2472 14.3704 15.8694 16.1836 17.3264 18C14.3921 17.4918 12.5158 16.0089 11.3559 14.6033C10.6547 14.742 9.92649 14.8235 9.17279 14.8235C4.10686 14.8235 0 11.5052 0 7.41177C0 3.31835 4.10686 0 9.17279 0Z'
                  fill='#646464'
                />
              </svg>
            </Box>
            {row.commentCount >= 1 && (
              <Box style={{ position: 'absolute', marginLeft: '22px', marginBottom: '12px' }}>
                <TaskBadge number={row.commentCount} variant='warning' size='small' />
              </Box>
            )}
          </div>
        );
      },
    },
    {
      field: 'roles',
      headerName: 'Roles',
      width: 115,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        return (
          <Typography style={{ fontWeight: 600, fontSize: '14px', textAlign: 'center', justifySelf: 'center' }}>
            Roles
          </Typography>
        );
      },
      renderCell: ({ row }: any) => {
        const noIcon = row.roles.filter((role: RoleType) => {
          return !role.roleIcon;
        });
        const icons = row.roles.filter((role: RoleType) => {
          return role.roleIcon;
        });

        if (icons.length > 0) {
          return (
            <Box
              style={{ display: 'flex', flex: '1', justifyContent: 'center', alignItems: 'center' }}
              onClick={() => profileOpenTrigger('profile', row.userId, row.profileId)}
            >
              {icons.map((role: RoleType) => (
                <Box
                  key={role.roleId}
                  marginRight='6px'
                  marginTop='4px'
                  paddingBottom='4px'
                  style={{ cursor: 'pointer' }}
                >
                  <Badge
                    overlap='circle'
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    badgeContent={
                      role.completed && (
                        <Box>
                          <CheckCircleIcon
                            style={{
                              color: '#60B386',
                              backgroundColor: '#ffffff',
                              borderRadius: '50%',
                              fontSize: '15px',
                            }}
                          />
                        </Box>
                      )
                    }
                  >
                    <Box width='26px' height='26px' display='flex' alignItems='center'>
                      <RoleIcon
                        iconName={role.roleIcon ?? ''}
                        size={'22px'}
                        color={'#646464'}
                        style={{ marginLeft: '2px', marginRight: '4px' }}
                      />
                    </Box>
                  </Badge>
                </Box>
              ))}
              {noIcon.length > 0 && (
                <Typography style={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                  + {noIcon.length} other/s
                </Typography>
              )}
            </Box>
          );
        }
        if (icons.length === 0 && noIcon.length > 0) {
          if (noIcon.length <= 3) {
            return (
              <Box
                style={{ display: 'flex', flex: '1' }}
                onClick={() => profileOpenTrigger('profile', row.userId, row.profileId)}
              >
                <Typography style={{ fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                  {noIcon.map((role: RoleType, i: number) => {
                    return i + 1 === noIcon.length ? `${role.roleName}` : `${role.roleName}, `;
                  })}
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              style={{ display: 'flex', flex: '1' }}
              onClick={() => profileOpenTrigger('profile', row.userId, row.profileId)}
            >
              <Typography style={{ fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                {noIcon.length} role/s
              </Typography>
            </Box>
          );
        }
        if (row.roles.length === 0) {
          return (
            <Box onClick={() => profileOpenTrigger('profile', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
              <Typography noWrap style={{ fontSize: '13px', color: '#646464' }}>
                No roles
              </Typography>
            </Box>
          );
        }
        return 'Error';
      },
    },
    {
      field: 'program',
      headerName: 'Program(s)',
      width: 200,
      disableColumnMenu: true,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Program(s)</Typography>;
      },
      renderCell: ({ row }: any) => {
        if (row.programs.length === 1) {
          return (
            <Box onClick={() => profileOpenTrigger('program', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.programs[0].programName}
              </Typography>
            </Box>
          );
        }
        if (row.programs.length === 2) {
          return (
            <Box onClick={() => profileOpenTrigger('program', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.programs[0].programName} + {row.programs.length - 1} other
              </Typography>
            </Box>
          );
        }
        if (row.programs.length > 2) {
          return (
            <Box onClick={() => profileOpenTrigger('program', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.programs[0].programName} + {row.programs.length - 1} others
              </Typography>
            </Box>
          );
        }
        return (
          <Box onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
            <Typography noWrap style={{ fontSize: '13px', color: '#646464' }}>
              No enrolments
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'activities',
      headerName: 'Activities',
      width: 200,
      disableColumnMenu: true,
      renderHeader: () => {
        return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Activities</Typography>;
      },
      renderCell: ({ row }: any) => {
        if (row.activities.length === 1 && row.activities[0].name.length > 25) {
          return (
            <Box
              onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)}
              style={{ cursor: 'pointer' }}
            >
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.activities.length} activity
              </Typography>
            </Box>
          );
        }
        if (row.activities.length > 1 && row.activities[0].name.length > 20) {
          return (
            <Box
              onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)}
              style={{ cursor: 'pointer' }}
            >
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.activities.length} activities
              </Typography>
            </Box>
          );
        }
        if (row.activities.length === 1) {
          return (
            <Box
              onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)}
              style={{ cursor: 'pointer' }}
            >
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.activities[0].name}
              </Typography>
            </Box>
          );
        }
        if (row.activities.length === 2) {
          return (
            <Box
              onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)}
              style={{ cursor: 'pointer' }}
            >
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.activities[0].name} + {row.activities.length - 1} other
              </Typography>
            </Box>
          );
        }
        if (row.activities.length > 2) {
          return (
            <Box
              onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)}
              style={{ cursor: 'pointer' }}
            >
              <Typography noWrap style={{ fontSize: '13px' }}>
                {row.activities[0].name} + {row.activities.length - 1} others
              </Typography>
            </Box>
          );
        }
        return (
          <Box onClick={() => profileOpenTrigger('activity', row.userId, row.profileId)} style={{ cursor: 'pointer' }}>
            <Typography noWrap style={{ fontSize: '13px', color: '#646464' }}>
              No enrolments
            </Typography>
          </Box>
        );
      },
    },
    ...(optionalColumns.waitlistings
      ? [
          {
            field: 'waitlistings',
            headerName: 'Waitlistings',
            width: 200,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Waitlistings</Typography>;
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              const items = row.waitlistings ?? [];
              if (items.length === 0) {
                return <Typography style={{ fontSize: '13px', color: '#9ca3af' }}>-</Typography>;
              }
              const display = items.map((w: { name: string }) => w.name).join(', ');
              return (
                <Typography noWrap style={{ fontSize: '13px' }} title={display}>
                  {display}
                </Typography>
              );
            },
          },
        ]
      : []),
    ...(optionalColumns.tags
      ? [
          {
            field: 'tags',
            headerName: 'Tags',
            width: 220,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Tags</Typography>;
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              const items: VolunteerTagCell[] = row.tags ?? [];
              if (items.length === 0) {
                return <Typography style={{ fontSize: '13px', color: '#9ca3af' }}>-</Typography>;
              }
              const visible = items.slice(0, 2);
              const overflow = items.slice(2);
              return (
                <Box
                  display='flex'
                  alignItems='center'
                  style={{ gap: 4, overflow: 'hidden' }}
                  title={items.map((tag) => tag.name).join(', ')}
                >
                  {visible.map((tag) => (
                    <Chip
                      key={tag.volunteerTagId}
                      label={tag.name}
                      size='small'
                      style={{
                        backgroundColor: tag.colour ?? '#796fff',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 500,
                        height: 22,
                        maxWidth: 110,
                        borderRadius: '4px',
                      }}
                    />
                  ))}
                  {overflow.length > 0 && (
                    <Typography style={{ fontSize: '12px', color: '#646464', whiteSpace: 'nowrap' }}>
                      {`+${overflow.length}`}
                    </Typography>
                  )}
                </Box>
              );
            },
          },
        ]
      : []),
    ...(optionalColumns.applications
      ? [
          {
            field: 'applications',
            headerName: 'Applications',
            width: 200,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Applications</Typography>;
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              const items = row.applications ?? [];
              if (items.length === 0) {
                return <Typography style={{ fontSize: '13px', color: '#9ca3af' }}>-</Typography>;
              }
              const display = items.map((a: { name: string }) => a.name).join(', ');
              return (
                <Typography noWrap style={{ fontSize: '13px' }} title={display}>
                  {display}
                </Typography>
              );
            },
          },
        ]
      : []),
    ...Array.from(
      new Map(
        (taskFieldFilter?.filters ?? [])
          .filter((filter) => filter.completionFilter)
          .map((filter) => [filter.taskId, filter.taskTitle] as [string, string])
      )
    ).map(([taskId, taskTitle]) => ({
      field: `taskStatus_${taskId}`,
      headerName: `${taskTitle} status`,
      width: 180,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => (
        <Typography noWrap style={{ fontWeight: 600, fontSize: '14px' }} title={`${taskTitle} status`}>
          {taskTitle} status
        </Typography>
      ),
      renderCell: (params: GridCellParams) => {
        const dateSubmitted: string | null | undefined = params.row.taskStatuses?.[taskId];
        if (!dateSubmitted) {
          return <Typography style={{ fontSize: '13px', color: '#b45309' }}>Incomplete</Typography>;
        }
        const label = `Completed · ${DateTime.fromISO(dateSubmitted).toFormat('d MMM yyyy')}`;
        return (
          <Typography noWrap style={{ fontSize: '13px', color: '#047857' }} title={label}>
            {label}
          </Typography>
        );
      },
    })),
    // Task field columns
    ...taskFieldColumns.map((fieldCol) => ({
      field: `taskField_${fieldCol.fieldId}`,
      headerName: fieldCol.fieldName,
      width: 150,
      disableColumnMenu: true,
      hideSortIcons: true,
      sortable: false,
      renderHeader: () => {
        return (
          <Typography
            style={{ fontWeight: 600, fontSize: '14px' }}
            title={`${fieldCol.taskTitle} - ${fieldCol.fieldName}`}
          >
            {fieldCol.fieldName}
          </Typography>
        );
      },
      renderCell: (params: GridCellParams) => {
        const { row } = params;
        const value = row.taskFieldValues?.[fieldCol.fieldId];
        if (value === null || value === undefined || value === '') {
          return <Typography style={{ fontSize: '13px', color: '#9ca3af' }}>-</Typography>;
        }
        // Format the value based on field type
        let displayValue = value;
        if (typeof value === 'object') {
          displayValue = JSON.stringify(value);
        }
        // Handle newline characters - replace with comma-separated for display
        if (typeof displayValue === 'string' && displayValue.includes('\n')) {
          displayValue = displayValue.split('\n').join(', ');
        }
        return (
          <Typography noWrap style={{ fontSize: '13px' }} title={String(displayValue)}>
            {String(displayValue)}
          </Typography>
        );
      },
    })),
    // Optional columns
    ...(optionalColumns.dateOfBirth
      ? [
          {
            field: 'dob',
            headerName: 'Date of Birth',
            width: 120,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return (
                <Box
                  onClick={() => {
                    setOrder('dob');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '14px' }}>DOB</Typography>
                  {orderBy === 'dob' ? (
                    orderDirection ? (
                      <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
                    ) : (
                      <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
                    )
                  ) : null}
                </Box>
              );
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              if (!row.dob) {
                return <Typography style={{ fontSize: '13px' }}>-</Typography>;
              }
              const dobDate = DateTime.fromJSDate(new Date(Date.parse(row.dob)));
              return <Typography style={{ fontSize: '13px' }}>{dobDate.toFormat('d MMM yyyy')}</Typography>;
            },
          },
        ]
      : []),
    ...(optionalColumns.description
      ? [
          {
            field: 'description',
            headerName: 'Description',
            flex: 1,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Description</Typography>;
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              const plainText = descriptionToPlainText(row.description);
              return (
                <Typography noWrap style={{ fontSize: '13px' }}>
                  {plainText || '-'}
                </Typography>
              );
            },
          },
        ]
      : []),
    ...(optionalColumns.volunteerHours
      ? [
          {
            field: 'volunteerHours',
            headerName: 'Hours',
            width: 90,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return (
                <Box
                  onClick={() => {
                    setOrder('hours');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Hours</Typography>
                  {orderBy === 'hours' ? (
                    orderDirection ? (
                      <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
                    ) : (
                      <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
                    )
                  ) : null}
                </Box>
              );
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              const hours = row.volunteerHours || 0;
              return <Typography style={{ fontSize: '13px' }}>{hours.toFixed(1)}</Typography>;
            },
          },
        ]
      : []),
    ...(optionalColumns.lastAttended
      ? [
          {
            field: 'lastAttended',
            headerName: 'Last Attended',
            width: 120,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return (
                <Box
                  onClick={() => {
                    setOrder('lastAttended');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Last Attended</Typography>
                  {orderBy === 'lastAttended' ? (
                    orderDirection ? (
                      <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
                    ) : (
                      <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
                    )
                  ) : null}
                </Box>
              );
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              if (!row.lastAttended || !row.lastAttended.date) {
                return <Typography style={{ fontSize: '13px' }}>-</Typography>;
              }
              const lastDate = DateTime.fromJSDate(new Date(Date.parse(row.lastAttended.date)));
              return <Typography style={{ fontSize: '13px' }}>{lastDate.toFormat('d MMM yyyy')}</Typography>;
            },
          },
        ]
      : []),
    ...(optionalColumns.totalAttendances
      ? [
          {
            field: 'totalAttendances',
            headerName: 'Total Attendances',
            width: 130,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return (
                <Box
                  onClick={() => {
                    setOrder('attendances');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Attendances</Typography>
                  {orderBy === 'attendances' ? (
                    orderDirection ? (
                      <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
                    ) : (
                      <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
                    )
                  ) : null}
                </Box>
              );
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              return <Typography style={{ fontSize: '13px' }}>{row.totalAttendances ?? '-'}</Typography>;
            },
          },
        ]
      : []),
    ...(optionalColumns.enrolledActivitiesCount
      ? [
          {
            field: 'enrolledActivitiesCount',
            headerName: '# Enrolled Activities',
            width: 130,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return (
                <Box
                  onClick={() => {
                    setOrder('activities');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '3px' }}
                >
                  <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Enrolled</Typography>
                  {orderBy === 'activities' ? (
                    orderDirection ? (
                      <ArrowUpward style={{ fontSize: '18px', color: '#464646' }} />
                    ) : (
                      <ArrowDownward style={{ fontSize: '18px', color: '#464646' }} />
                    )
                  ) : null}
                </Box>
              );
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              return <Typography style={{ fontSize: '13px' }}>{row.enrolledActivitiesCount ?? '-'}</Typography>;
            },
          },
        ]
      : []),
    ...(optionalColumns.lengthOfService
      ? [
          {
            field: 'lengthOfService',
            headerName: 'Length of Service',
            width: 140,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Service</Typography>;
            },
            renderCell: (params: GridCellParams) => {
              const { row } = params;
              return <Typography style={{ fontSize: '13px' }}>{row.lengthOfService ?? '-'}</Typography>;
            },
          },
        ]
      : []),
    ...(optionalColumns.attendanceRecords
      ? [
          {
            field: 'attendanceRecords',
            headerName: 'Attendance',
            width: 140,
            disableColumnMenu: true,
            hideSortIcons: true,
            sortable: false,
            renderHeader: () => {
              return <Typography style={{ fontWeight: 600, fontSize: '14px' }}>Attendance</Typography>;
            },
            renderCell: () => {
              return (
                <Typography style={{ fontSize: '12px', fontStyle: 'italic', color: theme.color.grey.neutral300 }}>
                  Export to see
                </Typography>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <Box display='flex' flexDirection='column' flex={1} height='100%' overflow='hidden'>
      <Box
        style={{
          paddingLeft: isMobile || isSm ? '10px' : '25px',
          paddingRight: '10px',
          paddingTop: '10px',
          display: 'flex',
          justifyContent: isMobile || isSm ? 'center' : 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {/* Title - hidden on mobile */}
        {!isMobile && !isSm && (
          <Typography style={{ fontSize: '14px', fontWeight: 700, color: '#333' }}>Database</Typography>
        )}

        {/* Buttons Container */}
        <Box
          style={{ display: 'flex', gap: isMobile || isSm ? '6px' : '10px', alignItems: 'center', flexWrap: 'wrap' }}
        >
          {/* Download Button */}
          <Button
            onClick={handleDownloadButtonClick}
            startIcon={<GetApp style={{ fontSize: '14px' }} />}
            style={{
              backgroundColor: 'transparent',
              color: '#646464',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              padding: '4.5px 12px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 'auto',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Exports
          </Button>

          {/* Actions Toggle */}
          <Button
            onClick={() => {
              setActionsEnabled(!actionsEnabled);
              if (actionsEnabled) clearSelectedVolunteers();
            }}
            startIcon={<Icon path={mdiCursorDefaultClick} size={0.6} color={actionsEnabled ? '#ffffff' : '#646464'} />}
            style={{
              backgroundColor: actionsEnabled ? '#646464' : 'transparent',
              color: actionsEnabled ? '#ffffff' : '#646464',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              padding: '4.5px 12px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 'auto',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Actions
          </Button>

          {/* Columns Dropdown */}
          <TooltipMenu
            open={openColumns}
            onClose={() => {
              setOpenColumns(false);
            }}
            title={
              <Box
                display='flex'
                flexDirection='column'
                alignItems='flex-start'
                style={{ padding: '5px 10px', justifyContent: 'flex-start', maxHeight: '50vh', overflowY: 'auto' }}
              >
                {/* Optional columns - user can toggle - shown at top */}
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.dateOfBirth}
                      onChange={() => toggleOptionalColumn('dateOfBirth')}
                    />
                  }
                  label='Date of Birth'
                  style={{ color: optionalColumns.dateOfBirth ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.description}
                      onChange={() => toggleOptionalColumn('description')}
                    />
                  }
                  label='Description'
                  style={{ color: optionalColumns.description ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.volunteerHours}
                      onChange={() => toggleOptionalColumn('volunteerHours')}
                    />
                  }
                  label='Volunteer Hours'
                  style={{ color: optionalColumns.volunteerHours ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.lastAttended}
                      onChange={() => toggleOptionalColumn('lastAttended')}
                    />
                  }
                  label='Last Attended'
                  style={{ color: optionalColumns.lastAttended ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.totalAttendances}
                      onChange={() => toggleOptionalColumn('totalAttendances')}
                    />
                  }
                  label='Total Attendances'
                  style={{ color: optionalColumns.totalAttendances ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.enrolledActivitiesCount}
                      onChange={() => toggleOptionalColumn('enrolledActivitiesCount')}
                    />
                  }
                  label='# Enrolled Activities'
                  style={{ color: optionalColumns.enrolledActivitiesCount ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.lengthOfService}
                      onChange={() => toggleOptionalColumn('lengthOfService')}
                    />
                  }
                  label='Length of Service'
                  style={{ color: optionalColumns.lengthOfService ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.waitlistings}
                      onChange={() => toggleOptionalColumn('waitlistings')}
                    />
                  }
                  label='Waitlistings'
                  style={{ color: optionalColumns.waitlistings ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.applications}
                      onChange={() => toggleOptionalColumn('applications')}
                    />
                  }
                  label='Applications'
                  style={{ color: optionalColumns.applications ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.tags}
                      onChange={() => toggleOptionalColumn('tags')}
                    />
                  }
                  label='Tags'
                  style={{ color: optionalColumns.tags ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.attendanceRecords}
                      onChange={() => toggleOptionalColumn('attendanceRecords')}
                    />
                  }
                  label='Attendance Records'
                  style={{ color: optionalColumns.attendanceRecords ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                {optionalColumns.attendanceRecords && (
                  <Box style={{ paddingLeft: '32px', paddingBottom: '8px', width: '100%' }}>
                    <Typography style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px', marginBottom: '4px' }}>
                      Date range (leave blank for all)
                    </Typography>
                    <Box style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type='date'
                        value={attendanceStartDate ?? ''}
                        onChange={(e) => {
                          setAttendanceStartDate(e.target.value || null);
                          clear();
                        }}
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          color: '#fff',
                          border: '1px solid rgba(255,255,255,0.3)',
                          borderRadius: '4px',
                          padding: '4px 6px',
                          fontSize: '11px',
                          colorScheme: 'dark',
                        }}
                      />
                      <input
                        type='date'
                        value={attendanceEndDate ?? ''}
                        onChange={(e) => {
                          setAttendanceEndDate(e.target.value || null);
                          clear();
                        }}
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          color: '#fff',
                          border: '1px solid rgba(255,255,255,0.3)',
                          borderRadius: '4px',
                          padding: '4px 6px',
                          fontSize: '11px',
                          colorScheme: 'dark',
                        }}
                      />
                    </Box>
                  </Box>
                )}
                {/* Divider */}
                <Box my={1} style={{ borderTop: '1px solid rgba(255,255,255,0.2)', width: '100%' }} />
                {/* Tasks - opens dialog */}
                <Button
                  startIcon={<Assignment style={{ fontSize: '16px' }} />}
                  endIcon={<ChevronRight style={{ fontSize: '16px' }} />}
                  onClick={() => {
                    setOpenColumns(false);
                    setTaskColumnSelectionOpen(true);
                  }}
                  style={{
                    color: '#fff',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 400,
                    padding: '4px 8px',
                    justifyContent: 'flex-start',
                    width: '100%',
                  }}
                >
                  Tasks {taskFieldColumns.length > 0 && `(${taskFieldColumns.length})`}
                </Button>
                {/* Divider */}
                <Box my={1} style={{ borderTop: '1px solid rgba(255,255,255,0.2)', width: '100%' }} />
                {/* Base columns - always visible and disabled */}
                <FormControlLabel
                  className={classes.disabledLabel}
                  control={<Checkbox checked disabled />}
                  label='Name'
                />
                <FormControlLabel
                  className={classes.disabledLabel}
                  control={<Checkbox checked disabled />}
                  label='Email'
                />
                <FormControlLabel
                  className={classes.disabledLabel}
                  control={<Checkbox checked disabled />}
                  label='Phone'
                />
                <FormControlLabel
                  className={classes.disabledLabel}
                  control={<Checkbox checked disabled />}
                  label='Status'
                />
                <FormControlLabel
                  className={classes.disabledLabel}
                  control={<Checkbox checked disabled />}
                  label='Created'
                />
                <FormControlLabel
                  className={classes.disabledLabel}
                  control={<Checkbox checked disabled />}
                  label='Role(s)'
                />
                <FormControlLabel
                  className={classes.disabledLabel}
                  control={<Checkbox checked disabled />}
                  label='Program(s)'
                />
                <FormControlLabel
                  className={classes.disabledLabel}
                  control={<Checkbox checked disabled />}
                  label='Activities'
                />
                {/* Export-only toggles */}
                <Box
                  style={{
                    borderTop: '1px solid rgba(255,255,255,0.15)',
                    width: '100%',
                    marginTop: '8px',
                    paddingTop: '6px',
                  }}
                >
                  <Typography
                    style={{
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '10px',
                      paddingLeft: '10px',
                      marginBottom: '2px',
                    }}
                  >
                    Include in export
                  </Typography>
                </Box>
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.volunteerId}
                      onChange={() => toggleOptionalColumn('volunteerId')}
                    />
                  }
                  label='Volunteer ID'
                  style={{ color: optionalColumns.volunteerId ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
                <FormControlLabel
                  className={classes.edit}
                  control={
                    <Checkbox
                      style={{ color: '#fff' }}
                      checked={optionalColumns.profileId}
                      onChange={() => toggleOptionalColumn('profileId')}
                    />
                  }
                  label='Profile ID'
                  style={{ color: optionalColumns.profileId ? 'rgba(255, 255, 255, 0.7)' : undefined }}
                />
              </Box>
            }
          >
            <Button
              startIcon={<ViewColumn style={{ fontSize: '14px' }} />}
              endIcon={<ExpandMore style={{ fontSize: '14px' }} />}
              onClick={() => {
                setOpenColumns(true);
              }}
              style={{
                backgroundColor: 'transparent',
                color: '#646464',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '4.5px 12px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 'auto',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Columns
            </Button>
          </TooltipMenu>

          {/* Tools Dropdown */}
          <TooltipMenu
            open={openTools}
            onClose={() => {
              setOpenTools(false);
            }}
            title={
              <Box
                display='flex'
                flexDirection='column'
                alignItems='flex-start'
                style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
              >
                <Button
                  startIcon={<LocationOn style={{ fontSize: '16px' }} />}
                  onClick={handleLocationSearchClick}
                  style={{
                    color: '#fff',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 400,
                    padding: '4px 8px',
                    justifyContent: 'flex-start',
                    width: '100%',
                  }}
                >
                  Location Search
                </Button>
                <Button
                  startIcon={<Assignment style={{ fontSize: '16px' }} />}
                  onClick={handleTaskFieldSearchClick}
                  style={{
                    color: '#fff',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 400,
                    padding: '4px 8px',
                    justifyContent: 'flex-start',
                    width: '100%',
                  }}
                >
                  Task Search
                </Button>
                <Button
                  startIcon={
                    <img
                      src={UserCheckinIcon}
                      alt=''
                      style={{ width: 14, height: 14, filter: 'brightness(0) invert(1)' }}
                    />
                  }
                  onClick={() => {
                    setCheckedInFilter(!checkedInFilter);
                    setOpenTools(false);
                    clear();
                  }}
                  style={{
                    color: '#fff',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 400,
                    padding: '4px 8px',
                    justifyContent: 'flex-start',
                    width: '100%',
                  }}
                >
                  Checked In
                </Button>
              </Box>
            }
          >
            <Button
              startIcon={<Build style={{ fontSize: '14px' }} />}
              endIcon={<ExpandMore style={{ fontSize: '14px' }} />}
              onClick={() => {
                setOpenTools(true);
              }}
              style={{
                backgroundColor: 'transparent',
                color: '#646464',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '4.5px 12px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 'auto',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Tools
            </Button>
          </TooltipMenu>
        </Box>
      </Box>
      <Box
        position='relative'
        display='flex'
        flexDirection='column'
        flex={1}
        overflow='auto'
        bgcolor='#fffffff'
        minHeight={0}
      >
        <Box
          style={{
            width: isMobile || isSm ? '95%' : '50%',
            marginBottom: isMobile || isSm ? '20px' : '40px',
            marginTop: '20px',
            marginLeft: 'auto',
            marginRight: 'auto',
            flexShrink: 0,
          }}
        >
          <Box style={{ display: 'flex', justifyContent: 'center' }}>
            <InputBase
              value={filterString}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  clear();
                }
              }}
              placeholder={isMobile || isSm ? 'Search by' : 'Search by name, email, phone number'}
              onChange={(e) => {
                setFilterString(e.target.value);
              }}
              style={{
                border: '.5px solid #d9d9d9',
                borderRadius: '25px 0 0 25px',
                width: '85%',
                display: 'flex',
                padding: '6px 24px',
              }}
            />
            <Button
              style={{
                backgroundColor: '#7047EB',
                borderRadius: '0 25px 25px 0',
                width: '12%',
                display: 'flex',
                padding: '8px 10px',
              }}
              onClick={() => clear()}
            >
              <Search style={{ color: '#fff' }} />
            </Button>
          </Box>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '12px',
              alignItems: 'center',
              flexWrap: isMobile || isSm ? 'wrap' : 'nowrap',
              gap: isMobile || isSm ? '8px' : undefined,
            }}
          >
            {/* Volunteers */}
            <TooltipMenu
              open={openVolunteers}
              onClose={() => {
                setOpenVolunteers(false);
              }}
              title={
                <RadioGroup
                  aria-label='includeInactive'
                  name='includeInactive'
                  value={includeInactive}
                  onChange={(e) => {
                    setIncludeInactive(e.target.value);
                    clear();
                  }}
                >
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='flex-start'
                    style={{ padding: '5px 10px', justifyContent: 'flex-start' }}
                  >
                    <FormControlLabel
                      className={classes.edit}
                      value='active'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='Active'
                    />
                    <FormControlLabel
                      className={classes.edit}
                      value='inactive'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='Inactive'
                    />
                    <FormControlLabel
                      className={classes.edit}
                      value='all'
                      control={<Radio style={{ color: '#fff' }} />}
                      label='All'
                    />
                  </Box>
                </RadioGroup>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenVolunteers(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{capitalize(includeInactive)}</strong>&nbsp;Volunteers
              </TabletButton>
            </TooltipMenu>
            {/* Programs */}
            <TooltipMenu
              open={openPrograms}
              onClose={() => {
                setOpenPrograms(false);
                setProgramSearchQuery('');
              }}
              title={
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='flex-start'
                  style={{ padding: '5px 10px', justifyContent: 'flex-start', maxHeight: '30vh' }}
                >
                  <Box
                    style={{
                      paddingTop: '6px',
                      paddingBottom: '4px',
                      width: '100%',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <Box style={{ display: 'flex', alignItems: 'center', padding: '4px 0px' }}>
                      <Search style={{ fontSize: '14px', color: '#fff', marginRight: '6px' }} />
                      <InputBase
                        placeholder='Search'
                        value={programSearchQuery}
                        onChange={(e) => setProgramSearchQuery(e.target.value)}
                        style={{ color: '#fff', fontSize: '12px', flex: 1 }}
                      />
                    </Box>
                  </Box>
                  <Box
                    style={{
                      overflowY: 'auto',
                      maxHeight: 'calc(30vh - 40px)',
                      width: '100%',
                    }}
                    sx={{
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {searchFilteredPrograms?.map((program) => {
                      return (
                        <FormControlLabel
                          key={program.programId}
                          className={classes.edit}
                          control={
                            <Checkbox
                              style={{ color: '#fff' }}
                              onChange={() => programFilterChange(program.programId)}
                            />
                          }
                          label={program.name}
                          name={program.programId}
                          checked={programFilter && programFilter.includes(program.programId)}
                        />
                      );
                    })}
                  </Box>
                  {programFilter.length > 0 && (
                    <Box
                      style={{
                        borderTop: '1px solid rgba(255,255,255,0.2)',
                        width: '100%',
                        paddingTop: '4px',
                        marginTop: '4px',
                      }}
                    >
                      <FormControlLabel
                        className={classes.edit}
                        control={
                          <Checkbox
                            style={{ color: '#fff' }}
                            checked={exclusiveProgramFilter}
                            onChange={() => {
                              setExclusiveProgramFilter(!exclusiveProgramFilter);
                              clear();
                            }}
                          />
                        }
                        label='Exclusive'
                        style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}
                      />
                    </Box>
                  )}
                </Box>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenPrograms(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{programFilter.length === 0 ? 'All' : programFilter.length}</strong>&nbsp;Programs
              </TabletButton>
            </TooltipMenu>
            {/* Activities */}
            <TooltipMenu
              open={openActivities}
              onClose={() => {
                setOpenActivities(false);
                setActivitySearchQuery('');
              }}
              title={
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='flex-start'
                  style={{ padding: '5px 10px', justifyContent: 'flex-start', maxHeight: '30vh' }}
                >
                  <Box
                    style={{
                      paddingTop: '6px',
                      paddingBottom: '4px',
                      width: '100%',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <Box style={{ display: 'flex', alignItems: 'center', padding: '4px 0px' }}>
                      <Search style={{ fontSize: '14px', color: '#fff', marginRight: '6px' }} />
                      <InputBase
                        placeholder='Search'
                        value={activitySearchQuery}
                        onChange={(e) => setActivitySearchQuery(e.target.value)}
                        style={{ color: '#fff', fontSize: '12px', flex: 1 }}
                      />
                    </Box>
                  </Box>
                  <Box
                    style={{
                      overflowY: 'auto',
                      maxHeight: 'calc(30vh - 40px)',
                      width: '100%',
                    }}
                    sx={{
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {searchFilteredActivities?.map((activity) => {
                      return (
                        <FormControlLabel
                          key={activity.activityId}
                          className={classes.edit}
                          control={
                            <Checkbox
                              style={{ color: '#fff' }}
                              onChange={() => activityFilterChange(activity.activityId)}
                            />
                          }
                          label={activity.name}
                          name={activity.activityId}
                          checked={activityFilter && activityFilter.includes(activity.activityId)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenActivities(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{activityFilter.length === 0 ? 'All' : activityFilter.length}</strong>&nbsp;Activities
              </TabletButton>
            </TooltipMenu>
            {/* Waitlisted Activities - only shown when Waitlistings column is active */}
            {optionalColumns.waitlistings && (
              <TooltipMenu
                open={openWaitlistActivities}
                onClose={() => {
                  setOpenWaitlistActivities(false);
                  setWaitlistActivitySearchQuery('');
                }}
                title={
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='flex-start'
                    style={{ padding: '5px 10px', justifyContent: 'flex-start', maxHeight: '30vh' }}
                  >
                    <Box
                      style={{
                        paddingTop: '6px',
                        paddingBottom: '4px',
                        width: '100%',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      <Box style={{ display: 'flex', alignItems: 'center', padding: '4px 0px' }}>
                        <Search style={{ fontSize: '14px', color: '#fff', marginRight: '6px' }} />
                        <InputBase
                          placeholder='Search'
                          value={waitlistActivitySearchQuery}
                          onChange={(e) => setWaitlistActivitySearchQuery(e.target.value)}
                          style={{ color: '#fff', fontSize: '12px', flex: 1 }}
                        />
                      </Box>
                    </Box>
                    <Box
                      style={{
                        overflowY: 'auto',
                        maxHeight: 'calc(30vh - 40px)',
                        width: '100%',
                      }}
                      sx={{
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(255, 255, 255, 0.3)',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: 'rgba(255, 255, 255, 0.5)',
                        },
                      }}
                    >
                      {searchFilteredWaitlistActivities?.map((activity) => {
                        return (
                          <FormControlLabel
                            key={activity.activityId}
                            className={classes.edit}
                            control={
                              <Checkbox
                                style={{ color: '#fff' }}
                                onChange={() => waitlistActivityFilterChange(activity.activityId)}
                              />
                            }
                            label={activity.name}
                            name={activity.activityId}
                            checked={waitlistActivityFilter.includes(activity.activityId)}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                }
              >
                <TabletButton
                  size='small'
                  aria-controls='options-menu'
                  aria-haspopup='true'
                  onClick={() => {
                    setOpenWaitlistActivities(true);
                  }}
                  endIcon={<ExpandMore />}
                  style={{
                    height: '100%',
                    border: 'none',
                    boxSizing: 'border-box',
                    padding: '2px 7px',
                    marginRight: '10px',
                    color: theme.color.grey.neutral300,
                  }}
                >
                  <strong>{waitlistActivityFilter.length === 0 ? 'All' : waitlistActivityFilter.length}</strong>
                  &nbsp;Waitlisted
                </TabletButton>
              </TooltipMenu>
            )}
            {/* Applied Activities - only shown when Applications column is active */}
            {optionalColumns.applications && (
              <TooltipMenu
                open={openApplicationActivities}
                onClose={() => {
                  setOpenApplicationActivities(false);
                  setApplicationActivitySearchQuery('');
                }}
                title={
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='flex-start'
                    style={{ padding: '5px 10px', justifyContent: 'flex-start', maxHeight: '30vh' }}
                  >
                    <Box
                      style={{
                        paddingTop: '6px',
                        paddingBottom: '4px',
                        width: '100%',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      <Box style={{ display: 'flex', alignItems: 'center', padding: '4px 0px' }}>
                        <Search style={{ fontSize: '14px', color: '#fff', marginRight: '6px' }} />
                        <InputBase
                          placeholder='Search'
                          value={applicationActivitySearchQuery}
                          onChange={(e) => setApplicationActivitySearchQuery(e.target.value)}
                          style={{ color: '#fff', fontSize: '12px', flex: 1 }}
                        />
                      </Box>
                    </Box>
                    <Box
                      style={{
                        overflowY: 'auto',
                        maxHeight: 'calc(30vh - 40px)',
                        width: '100%',
                      }}
                      sx={{
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(255, 255, 255, 0.3)',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: 'rgba(255, 255, 255, 0.5)',
                        },
                      }}
                    >
                      {searchFilteredApplicationActivities?.map((activity) => {
                        return (
                          <FormControlLabel
                            key={activity.activityId}
                            className={classes.edit}
                            control={
                              <Checkbox
                                style={{ color: '#fff' }}
                                onChange={() => applicationActivityFilterChange(activity.activityId)}
                              />
                            }
                            label={activity.name}
                            name={activity.activityId}
                            checked={applicationActivityFilter.includes(activity.activityId)}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                }
              >
                <TabletButton
                  size='small'
                  aria-controls='options-menu'
                  aria-haspopup='true'
                  onClick={() => {
                    setOpenApplicationActivities(true);
                  }}
                  endIcon={<ExpandMore />}
                  style={{
                    height: '100%',
                    border: 'none',
                    boxSizing: 'border-box',
                    padding: '2px 7px',
                    marginRight: '10px',
                    color: theme.color.grey.neutral300,
                  }}
                >
                  <strong>{applicationActivityFilter.length === 0 ? 'All' : applicationActivityFilter.length}</strong>
                  &nbsp;Applied
                </TabletButton>
              </TooltipMenu>
            )}
            {/* Roles */}
            <TooltipMenu
              open={openRoles}
              onClose={() => {
                setOpenRoles(false);
                setRoleSearchQuery('');
              }}
              title={
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='flex-start'
                  style={{ padding: '5px 10px', justifyContent: 'flex-start', maxHeight: '30vh' }}
                >
                  <Box
                    style={{
                      paddingTop: '6px',
                      paddingBottom: '4px',
                      width: '100%',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <Box style={{ display: 'flex', alignItems: 'center', padding: '4px 0px' }}>
                      <Search style={{ fontSize: '14px', color: '#fff', marginRight: '6px' }} />
                      <InputBase
                        placeholder='Search'
                        value={roleSearchQuery}
                        onChange={(e) => setRoleSearchQuery(e.target.value)}
                        style={{ color: '#fff', fontSize: '12px', flex: 1 }}
                      />
                    </Box>
                  </Box>
                  <Box
                    style={{
                      overflowY: 'auto',
                      maxHeight: 'calc(30vh - 40px)',
                      width: '100%',
                    }}
                    sx={{
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {searchFilteredRoles?.map((role) => {
                      return (
                        <FormControlLabel
                          key={role.activityRoleId}
                          className={classes.edit}
                          control={
                            <Checkbox
                              style={{ color: '#fff' }}
                              onChange={() => roleFilterChange(role.activityRoleId)}
                            />
                          }
                          label={
                            <Box>
                              <RoleIcon style={{ height: '12px', paddingRight: '3px' }} iconName={role.icon ?? ''} />{' '}
                              {role.name}
                            </Box>
                          }
                          name={role.activityRoleId}
                          checked={roleFilter && roleFilter.includes(role.activityRoleId)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenRoles(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{roleFilter.length === 0 ? 'All' : roleFilter.length}</strong>&nbsp;Roles
              </TabletButton>
            </TooltipMenu>
            {/* Tags */}
            <TooltipMenu
              open={openTags}
              onClose={() => {
                setOpenTags(false);
                setTagSearchQuery('');
              }}
              title={
                <Box display='flex' flexDirection='column' style={{ padding: '5px 10px', minWidth: '220px' }}>
                  <Box display='flex' style={{ gap: '4px', marginBottom: '8px' }}>
                    {(['any', 'all'] as const).map((logic) => (
                      <Box
                        key={logic}
                        onClick={() => tagFilterLogicChange(logic)}
                        style={{
                          flex: 1,
                          textAlign: 'center',
                          fontSize: '11px',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          padding: '4px 0',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          color: tagFilterLogic === logic ? '#fff' : 'rgba(255,255,255,0.6)',
                          backgroundColor: tagFilterLogic === logic ? '#7047EB' : 'rgba(255,255,255,0.11)',
                        }}
                      >
                        {logic === 'any' ? 'MATCH ANY' : 'MATCH ALL'}
                      </Box>
                    ))}
                  </Box>
                  <Typography style={{ fontSize: '10.5px', color: 'rgba(255,255,255,0.45)', marginBottom: '8px' }}>
                    {tagFilterLogic === 'all'
                      ? 'Volunteers must have every tag selected below.'
                      : 'Volunteers with any of the tags selected below.'}
                  </Typography>
                  <InputBase
                    value={tagSearchQuery}
                    placeholder='Search tags'
                    onChange={(e) => setTagSearchQuery(e.target.value)}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.12)',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      marginBottom: '8px',
                      color: '#fff',
                      fontSize: '13px',
                    }}
                  />
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='flex-start'
                    style={{ maxHeight: '260px', overflowY: 'auto' }}
                  >
                    {searchFilteredTags.map((tag) => {
                      return (
                        <FormControlLabel
                          key={tag.volunteerTagId}
                          className={classes.edit}
                          control={
                            <Checkbox style={{ color: '#fff' }} onChange={() => tagFilterChange(tag.volunteerTagId)} />
                          }
                          label={
                            <Box display='flex' alignItems='center'>
                              <Box
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  marginRight: 6,
                                  flexShrink: 0,
                                  backgroundColor: tag.colour ?? '#796fff',
                                }}
                              />
                              {tag.name}
                            </Box>
                          }
                          name={tag.volunteerTagId}
                          checked={tagFilter.includes(tag.volunteerTagId)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              }
            >
              <TabletButton
                size='small'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => {
                  setOpenTags(true);
                }}
                endIcon={<ExpandMore />}
                style={{
                  height: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                  padding: '2px 7px',
                  marginRight: '10px',
                  color: theme.color.grey.neutral300,
                }}
              >
                <strong>{tagFilter.length === 0 ? 'All' : tagFilter.length}</strong>&nbsp;Tags
              </TabletButton>
            </TooltipMenu>
            {locationFilter && (
              <div>
                <TabletButton
                  size='small'
                  onClick={() => {
                    // Open dialog to edit the filter
                    setLocationSearchOpen(true);
                  }}
                  startIcon={
                    <Close
                      style={{ fontSize: '16px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Remove the filter
                        setLocationFilter(null);
                        setOrderBy('name');
                        setOrderDirection(true);
                        clear();
                      }}
                    />
                  }
                  style={{
                    height: '100%',
                    border: 'none',
                    boxSizing: 'border-box',
                    padding: '2px 10px',
                    marginRight: '10px',
                    backgroundColor: '#7047EB',
                    color: 'white',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <strong>Location</strong>
                </TabletButton>
              </div>
            )}
            {taskFieldFilter && (
              <div>
                <TabletButton
                  size='small'
                  onClick={() => {
                    // Open dialog to edit the filter
                    setTaskFieldSearchOpen(true);
                  }}
                  startIcon={
                    <Close
                      style={{ fontSize: '16px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Remove the filter
                        setTaskFieldFilter(null);
                        // Remove only the task field columns that were added by the search
                        setTaskFieldColumns((prev) => prev.filter((col) => !searchAddedFieldIds.has(col.fieldId)));
                        setSearchAddedFieldIds(new Set());
                        clear();
                      }}
                    />
                  }
                  style={{
                    height: '100%',
                    border: 'none',
                    boxSizing: 'border-box',
                    padding: '2px 10px',
                    marginRight: '10px',
                    backgroundColor: '#7047EB',
                    color: 'white',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <strong>Tasks ({taskFieldFilter.filters.length})</strong>
                </TabletButton>
              </div>
            )}
            {checkedInFilter && (
              <div>
                <TabletButton
                  size='small'
                  startIcon={
                    <Close
                      style={{ fontSize: '16px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCheckedInFilter(false);
                        clear();
                      }}
                    />
                  }
                  style={{
                    height: '100%',
                    border: 'none',
                    boxSizing: 'border-box',
                    padding: '2px 10px',
                    marginRight: '10px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <strong>Checked In</strong>
                </TabletButton>
              </div>
            )}
            <TabletButton
              size='small'
              aria-controls='options-menu'
              aria-haspopup='true'
              onClick={() => {
                reset();
              }}
              startIcon={<Refresh />}
              style={{
                height: '100%',
                border: 'none',
                boxSizing: 'border-box',
                padding: '2px 7px',
                marginRight: '10px',
                color: theme.color.grey.neutral300,
              }}
            >
              <strong>Reset</strong>
            </TabletButton>
          </Box>
        </Box>
        <Box
          style={{
            width: '98%',
            marginLeft: 'auto',
            marginRight: 'auto',
            overflowX: 'auto',
            flex: 1,
            minHeight: isMobile || isSm ? '400px' : '300px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DataGrid
            columns={columns}
            pagination
            onPageSizeChange={(pageSize) => updateLimit(pageSize)}
            pageSize={limit}
            rows={(volunteerDatabase?.volunteers ?? []).filter((v) => v.email !== 'support@volaby.org')}
            loading={loading}
            page={currentPage}
            rowsPerPageOptions={[25]}
            disableSelectionOnClick
            checkboxSelection={actionsEnabled}
            selectionModel={currentPageSelectionModel}
            onSelectionModelChange={(newSelection) => handleSelectionModelChange(newSelection)}
            rowCount={volunteerDatabase?.total}
            rowHeight={70}
            headerHeight={30}
            onPageChange={(e) => {
              changePage(e);
            }}
            components={{
              NoRowsOverlay: () => (
                <Box
                  height='100%'
                  width='100%'
                  alignItems='center'
                  justifyContent='center'
                  display={'flex'}
                  flexDirection='column'
                  style={{ color: '#464646' }}
                >
                  {error ? (
                    <>
                      <Warning style={{ fontSize: '48px', color: '#ff9800', marginBottom: '16px' }} />
                      <Typography variant='h6' style={{ marginBottom: '8px', color: '#ff9800' }}>
                        Warning
                      </Typography>
                      <Typography variant='body2' style={{ textAlign: 'center', maxWidth: '400px' }}>
                        {error}
                      </Typography>
                      <Button
                        onClick={() => fetch()}
                        style={{ marginTop: '16px', textTransform: 'none' }}
                        variant='outlined'
                        size='small'
                      >
                        Try Again
                      </Button>
                    </>
                  ) : (
                    <>
                      <span role='img' aria-label="Can't find" style={{ fontSize: '24px', marginBottom: '8px' }}>
                        😕
                      </span>
                      No results found
                    </>
                  )}
                </Box>
              ),
              NoResultsOverlay: () => (
                <Box
                  height='100%'
                  width='100%'
                  alignItems='center'
                  justifyContent='center'
                  display={'flex'}
                  style={{ color: '#464646' }}
                >
                  <span role='img' aria-label="Can't find">
                    😕&nbsp;&nbsp;&nbsp;
                  </span>
                  No results found
                </Box>
              ),
              Footer: () => {
                const actionBtnStyle = {
                  backgroundColor: 'transparent',
                  color: '#646464',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  padding: '4.5px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'none' as const,
                  minWidth: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                };
                const menuItemStyle = {
                  color: '#fff',
                  textTransform: 'none' as const,
                  fontSize: '14px',
                  fontWeight: 400,
                  padding: '4px 8px',
                  justifyContent: 'flex-start',
                  width: '100%',
                };
                const showActions = actionsEnabled && selectedVolunteerIds.length > 0;
                return (
                  <GridFooterContainer style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    {showActions && (
                      <>
                        <Box
                          display='flex'
                          alignItems='center'
                          style={{ gap: '6px', paddingLeft: '8px', whiteSpace: 'nowrap' as const }}
                        >
                          <Typography variant='body2'>{selectedVolunteerIds.length} selected</Typography>
                          <Button
                            size='small'
                            onClick={() => clearSelectedVolunteers()}
                            style={{
                              color: theme.color.grey.neutral300,
                              textTransform: 'none',
                              fontSize: '11px',
                              minWidth: 'auto',
                              padding: '2px 6px',
                            }}
                          >
                            Deselect
                          </Button>
                        </Box>
                        <Box
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                          style={{
                            gap: '8px',
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1,
                          }}
                        >
                          <TooltipMenu
                            open={openActionComms}
                            onClose={() => setOpenActionComms(false)}
                            title={
                              <Box
                                display='flex'
                                flexDirection='column'
                                alignItems='flex-start'
                                style={{ padding: '5px 10px' }}
                              >
                                <Button
                                  startIcon={<Email style={{ fontSize: '16px' }} />}
                                  onClick={() => {
                                    closeAllActionMenus();
                                    setBulkEmailOpen(true);
                                  }}
                                  style={menuItemStyle}
                                >
                                  Email
                                </Button>
                                <Button
                                  startIcon={<VpnKey style={{ fontSize: '16px' }} />}
                                  onClick={() => {
                                    closeAllActionMenus();
                                    setPasswordResetOpen(true);
                                  }}
                                  style={menuItemStyle}
                                >
                                  Password Reset
                                </Button>
                                {orgInfo?.smsEnabled && (
                                  <Button
                                    startIcon={<Sms style={{ fontSize: '16px' }} />}
                                    onClick={() => {
                                      closeAllActionMenus();
                                      setBulkSmsOpen(true);
                                    }}
                                    style={menuItemStyle}
                                  >
                                    SMS
                                  </Button>
                                )}
                                {orgInfo?.chatEnabled && (
                                  <Button
                                    startIcon={<Forum style={{ fontSize: '16px' }} />}
                                    onClick={() => {
                                      closeAllActionMenus();
                                      setCreateChatOpen(true);
                                    }}
                                    style={menuItemStyle}
                                  >
                                    Create Chat
                                  </Button>
                                )}
                              </Box>
                            }
                          >
                            <Button
                              startIcon={<Email style={{ fontSize: '14px' }} />}
                              endIcon={<ExpandMore style={{ fontSize: '14px' }} />}
                              onClick={() => {
                                closeAllActionMenus();
                                setOpenActionComms(true);
                              }}
                              style={actionBtnStyle}
                            >
                              Communicate
                            </Button>
                          </TooltipMenu>

                          <TooltipMenu
                            open={openActionPrograms}
                            onClose={() => setOpenActionPrograms(false)}
                            title={
                              <Box
                                display='flex'
                                flexDirection='column'
                                alignItems='flex-start'
                                style={{ padding: '5px 10px' }}
                              >
                                <Button
                                  startIcon={<PersonAdd style={{ fontSize: '16px' }} />}
                                  onClick={() => {
                                    closeAllActionMenus();
                                    setBulkProgramEnrolOpen(true);
                                  }}
                                  style={menuItemStyle}
                                >
                                  Enrol
                                </Button>
                              </Box>
                            }
                          >
                            <Button
                              startIcon={<Explore style={{ fontSize: '14px' }} />}
                              endIcon={<ExpandMore style={{ fontSize: '14px' }} />}
                              onClick={() => {
                                closeAllActionMenus();
                                setOpenActionPrograms(true);
                              }}
                              style={actionBtnStyle}
                            >
                              Programs
                            </Button>
                          </TooltipMenu>

                          <TooltipMenu
                            open={openActionActivities}
                            onClose={() => setOpenActionActivities(false)}
                            title={
                              <Box
                                display='flex'
                                flexDirection='column'
                                alignItems='flex-start'
                                style={{ padding: '5px 10px' }}
                              >
                                <Button
                                  startIcon={<PersonAdd style={{ fontSize: '16px' }} />}
                                  onClick={() => {
                                    closeAllActionMenus();
                                    if (sharedProgramIds.size === 0) {
                                      setSnackbar({
                                        open: true,
                                        message:
                                          'Selected volunteers share no common program. They must be enrolled in the same program before enrolling in its activities.',
                                        variant: 'error',
                                      });
                                      return;
                                    }
                                    setBulkActivityEnrolOpen(true);
                                  }}
                                  style={menuItemStyle}
                                >
                                  Enrol
                                </Button>
                              </Box>
                            }
                          >
                            <Button
                              startIcon={<Event style={{ fontSize: '14px' }} />}
                              endIcon={<ExpandMore style={{ fontSize: '14px' }} />}
                              onClick={() => {
                                closeAllActionMenus();
                                setOpenActionActivities(true);
                              }}
                              style={actionBtnStyle}
                            >
                              Activities
                            </Button>
                          </TooltipMenu>

                          <TooltipMenu
                            open={openActionPeople}
                            onClose={() => setOpenActionPeople(false)}
                            title={
                              <Box
                                display='flex'
                                flexDirection='column'
                                alignItems='flex-start'
                                style={{ padding: '5px 10px' }}
                              >
                                {peopleActions.map((action) => (
                                  <React.Fragment key={action.label}>
                                    {action.dividerBefore && (
                                      <Box
                                        style={{
                                          height: 1,
                                          alignSelf: 'stretch',
                                          margin: '5px 8px',
                                          backgroundColor: 'rgba(255,255,255,0.15)',
                                        }}
                                      />
                                    )}
                                    <Button
                                      startIcon={action.icon}
                                      onClick={() => {
                                        closeAllActionMenus();
                                        action.onSelect();
                                      }}
                                      style={menuItemStyle}
                                    >
                                      {action.label}
                                    </Button>
                                  </React.Fragment>
                                ))}
                              </Box>
                            }
                          >
                            <Button
                              startIcon={<EmojiPeople style={{ fontSize: '14px' }} />}
                              endIcon={<ExpandMore style={{ fontSize: '14px' }} />}
                              onClick={() => {
                                closeAllActionMenus();
                                setOpenActionPeople(true);
                              }}
                              style={actionBtnStyle}
                            >
                              People
                            </Button>
                          </TooltipMenu>

                          <TooltipMenu
                            open={openActionTasks}
                            onClose={() => setOpenActionTasks(false)}
                            title={
                              <Box
                                display='flex'
                                flexDirection='column'
                                alignItems='flex-start'
                                style={{ padding: '5px 10px' }}
                              >
                                <Button
                                  startIcon={<CheckCircleIcon style={{ fontSize: '16px' }} />}
                                  onClick={() => {
                                    closeAllActionMenus();
                                    setBulkTaskAction('approved');
                                    setBulkTaskOpen(true);
                                  }}
                                  style={menuItemStyle}
                                >
                                  Complete
                                </Button>
                                <Button
                                  startIcon={<NotInterested style={{ fontSize: '16px' }} />}
                                  onClick={() => {
                                    closeAllActionMenus();
                                    setBulkTaskAction('not-applicable');
                                    setBulkTaskOpen(true);
                                  }}
                                  style={menuItemStyle}
                                >
                                  Mark as N/A
                                </Button>
                              </Box>
                            }
                          >
                            <Button
                              startIcon={<Assignment style={{ fontSize: '14px' }} />}
                              endIcon={<ExpandMore style={{ fontSize: '14px' }} />}
                              onClick={() => {
                                closeAllActionMenus();
                                setOpenActionTasks(true);
                              }}
                              style={actionBtnStyle}
                            >
                              Tasks
                            </Button>
                          </TooltipMenu>

                          {orgInfo?.cicoEnabled && (
                            <TooltipMenu
                              open={openActionAttendance}
                              onClose={() => setOpenActionAttendance(false)}
                              title={
                                <Box
                                  display='flex'
                                  flexDirection='column'
                                  alignItems='flex-start'
                                  style={{ padding: '5px 10px' }}
                                >
                                  <Button
                                    startIcon={
                                      <img
                                        src={UserCheckinLightIcon}
                                        alt=''
                                        style={{ width: '16px', height: '16px' }}
                                      />
                                    }
                                    onClick={() => {
                                      closeAllActionMenus();
                                      setBulkCheckInOpen(true);
                                    }}
                                    style={menuItemStyle}
                                  >
                                    Check In
                                  </Button>
                                  <Button
                                    startIcon={
                                      <img
                                        src={UserCheckoutLightIcon}
                                        alt=''
                                        style={{ width: '16px', height: '16px' }}
                                      />
                                    }
                                    onClick={() => {
                                      closeAllActionMenus();
                                      setBulkCheckOutOpen(true);
                                    }}
                                    style={menuItemStyle}
                                  >
                                    Check Out
                                  </Button>
                                </Box>
                              }
                            >
                              <Button
                                startIcon={<HowToReg style={{ fontSize: '14px' }} />}
                                endIcon={<ExpandMore style={{ fontSize: '14px' }} />}
                                onClick={() => {
                                  closeAllActionMenus();
                                  setOpenActionAttendance(true);
                                }}
                                style={actionBtnStyle}
                              >
                                Attendance
                              </Button>
                            </TooltipMenu>
                          )}
                        </Box>
                      </>
                    )}
                    <GridPagination />
                  </GridFooterContainer>
                );
              },
            }}
            className={classes.hideRightSeparator}
            paginationMode='server'
            style={{
              fontSize: '14px',
              border: 'none',
              flex: 1,
              minHeight: 0,
            }}
          />
        </Box>

        {profileOpen && openProfileId && openUserId && (
          <VolunteerCommonProfileDialog
            key={openProfileId}
            profileId={openProfileId}
            userId={openUserId}
            open={profileOpen}
            openOn={openProfilePage}
            onClose={() => setProfileOpen(false)}
          />
        )}
      </Box>

      {/* Exports Popover */}
      <Popover
        open={Boolean(exportsPopoverAnchor)}
        anchorEl={exportsPopoverAnchor}
        onClose={handleExportsPopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 600,
            padding: '8px',
          },
        }}
      >
        <ExportsList exportType='volunteer' onCreate={handleCsvDownloadRequest} />
      </Popover>

      {/* Location Search Dialog */}
      <LocationSearchDialog
        open={locationSearchOpen}
        onClose={() => setLocationSearchOpen(false)}
        onSearch={handleLocationSearch}
      />

      {/* Task Field Search Dialog */}
      <TaskFieldSearchDialog
        open={taskFieldSearchOpen}
        onClose={() => setTaskFieldSearchOpen(false)}
        onSearch={handleTaskFieldSearch}
        initialFilters={taskFieldFilter}
        tasksData={tasksWithFieldsData}
      />

      {/* Task Column Selection Dialog */}
      <TaskColumnSelectionDialog
        open={taskColumnSelectionOpen}
        onClose={() => setTaskColumnSelectionOpen(false)}
        onSave={handleTaskColumnSelection}
        initialSelectedFields={taskFieldColumns}
        tasksData={tasksWithFieldsData}
      />

      {bulkEmailOpen && selectedVolunteersForDialog.length > 0 && (
        <VolunteerCommonProfileEmailDialog
          volunteers={selectedVolunteersForDialog}
          open={bulkEmailOpen}
          onClose={() => setBulkEmailOpen(false)}
        />
      )}

      {passwordResetOpen && bulkVolunteerIds.length > 0 && (
        <SendPasswordResetDialog
          open={passwordResetOpen}
          onClose={() => setPasswordResetOpen(false)}
          volunteerIds={bulkVolunteerIds}
          onSuccess={() => setPasswordResetOpen(false)}
        />
      )}

      {bulkSmsOpen && selectedVolunteersForDialog.length > 0 && (
        <VolunteerCommonProfileSmsDialog
          volunteers={selectedVolunteersForDialog}
          open={bulkSmsOpen}
          onClose={() => setBulkSmsOpen(false)}
        />
      )}

      {createChatOpen && (
        <CreateChannelDialog
          open={createChatOpen}
          onClose={() => setCreateChatOpen(false)}
          onCreated={() => setCreateChatOpen(false)}
        />
      )}

      {bulkProgramEnrolOpen && filteredProgramIds && (
        <ProgramFilters
          programs={filteredProgramIds}
          selectedPrograms={[]}
          setSelectedPrograms={handleBulkProgramEnrol}
          open={bulkProgramEnrolOpen}
          onClose={() => setBulkProgramEnrolOpen(false)}
          onCancel={() => setBulkProgramEnrolOpen(false)}
          fullScreen={false}
          simple
          addVol
        />
      )}

      {bulkActivityEnrolOpen && activitiesForBulkEnrol && (
        <ActivityFilters
          activities={activitiesForBulkEnrol}
          selectedActivities={[]}
          setSelectedActivities={handleBulkActivityEnrol}
          open={bulkActivityEnrolOpen}
          onClose={() => setBulkActivityEnrolOpen(false)}
          onCancel={() => setBulkActivityEnrolOpen(false)}
          fullScreen={false}
          simple
          addVol
        />
      )}

      {bulkRoleEnrolOpen && activityRoles && (
        <BulkRoleEnrolDialog
          open={bulkRoleEnrolOpen}
          onClose={() => setBulkRoleEnrolOpen(false)}
          volunteerIds={bulkVolunteerIds}
          roles={activityRoles}
          onSuccess={() => fetch()}
        />
      )}

      {bulkTagMode && (
        <BulkTagDialog
          open={bulkTagMode !== null}
          mode={bulkTagMode}
          onClose={() => setBulkTagMode(null)}
          volunteerIds={bulkVolunteerIds}
          tags={volunteerTagOptions}
          onSuccess={() => fetch()}
        />
      )}

      {bulkTaskOpen && tasksWithFieldsData?.vm.tasks && (
        <BulkTaskActionDialog
          open={bulkTaskOpen}
          onClose={() => setBulkTaskOpen(false)}
          onCancel={() => setBulkTaskOpen(false)}
          tasks={tasksWithFieldsData.vm.tasks}
          profileIds={bulkProfileIds}
          action={bulkTaskAction}
          onSuccess={() => fetch()}
        />
      )}

      {bulkCheckInOpen && (
        <BulkCheckInDialog
          open={bulkCheckInOpen}
          onClose={() => setBulkCheckInOpen(false)}
          volunteers={bulkAttendanceVolunteers}
          onComplete={() => fetch()}
        />
      )}

      {bulkCheckOutOpen && (
        <BulkCheckOutDialog
          open={bulkCheckOutOpen}
          onClose={() => setBulkCheckOutOpen(false)}
          volunteers={bulkAttendanceVolunteers}
          onComplete={() => fetch()}
        />
      )}
    </Box>
  );
});

export default VolunteerDatabaseScreenSimple;
